import fscreen from 'fscreen';
import { useCallback, useEffect, useState } from 'react';
import { useEventListener } from './useEventListener';

const useFullscreen = (refObj: any) => {
  const ref = refObj?.current;
  const isFullscreenAvailable = fscreen?.fullscreenEnabled === true;
  const [isFullscreen, setIsFullscreen] = useState(isFullscreenAvailable ? false : undefined);

  const toggleFullscreen = useCallback(
    async (fallbackNativeToggleFullscreen?: () => void) => {
      if (isFullscreenAvailable) {
        if (fscreen.fullscreenElement !== ref) {
          // this prevents the state change when there's an error (doesn't work on safari though)
          await fscreen.requestFullscreen(ref);
          setIsFullscreen(true);
        } else {
          await fscreen.exitFullscreen();
          setIsFullscreen(false);
        }
      } else if (fallbackNativeToggleFullscreen) {
        fallbackNativeToggleFullscreen();
      }
    },
    [isFullscreenAvailable, ref],
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.code === 'KeyF') {
        toggleFullscreen();
      }
    },
    [toggleFullscreen],
  );

  useEffect(() => {
    if (ref) {
      ref.addEventListener('keydown', handleKeyDown);
      return () => {
        ref.removeEventListener('keydown', handleKeyDown);
      };
    }
    return undefined;
  }, [handleKeyDown, ref]);

  const onFullscreenChange = useCallback(() => {
    setIsFullscreen(fscreen.fullscreenElement === ref);
  }, [ref, setIsFullscreen]);

  useEventListener(onFullscreenChange, 'fullscreenchange', ref);

  return [isFullscreen, toggleFullscreen as any, isFullscreenAvailable];
};

export default useFullscreen;
